import { useMemo, useState } from "react";
import SearchableSelect from "./SearchableSelect";
import { MultiSelect, useReceivableLookups } from "./receivables/common";

/** "CODE - Name" when the customer has a code (retailer code), else the name. */
export const customerLabel = (customer) =>
  customer ? `${customer.retailerCode ? `${customer.retailerCode} - ` : ""}${customer.name || ""}` : "";

/** Beats of the salesman (all beats when none is picked). */
export const beatsOfSalesman = (beats, salesmanUserId) =>
  salesmanUserId ? beats.filter((b) => String(b.salesmanUserId) === String(salesmanUserId)) : beats;

/**
 * The customers on the picked beats; with only a salesman picked, on any of the salesman's beats; with neither, all of them.
 * Customers need a `beatId`.
 */
export const customersInScope = (customers, beats, { salesmanUserId, beatIds }) => {
  if (beatIds && beatIds.length) return customers.filter((c) => beatIds.map(String).includes(String(c.beatId)));
  if (salesmanUserId) {
    const ids = new Set(beatsOfSalesman(beats, salesmanUserId).map((b) => String(b.id)));
    return customers.filter((c) => ids.has(String(c.beatId)));
  }
  return customers;
};

/** Salesman dropdown and a multi-select of that salesman's beats. `scope` = { salesmanUserId, beatIds }. */
export const CustomerScopeFilters = ({ scope, onChange, salesmen, beats, compact = false }) => (
  <>
    <select
      value={scope.salesmanUserId || ""}
      onChange={(e) => onChange({ salesmanUserId: e.target.value, beatIds: [] })}
      aria-label="Salesman"
      style={compact ? { maxWidth: 180 } : undefined}
    >
      <option value="">All salesmen</option>
      {salesmen.map((s) => (
        <option key={s.userId} value={s.userId}>
          {s.name}
        </option>
      ))}
    </select>
    <MultiSelect
      options={beatsOfSalesman(beats, scope.salesmanUserId).map((b) => ({ value: b.id, label: b.name }))}
      selected={scope.beatIds || []}
      onChange={(beatIds) => onChange({ ...scope, beatIds })}
      placeholder={scope.salesmanUserId ? "All beats of salesman" : "All beats"}
      width={200}
    />
  </>
);

/**
 * A customer dropdown with salesman and beat filters in front of it: picking a salesman limits the beats to theirs, picking
 * beats (several allowed) limits the customers to those beats. The dropdown is searched by customer code or name and shows the
 * customer's beat and salesman. `customers` are full customer records (id, name, retailerCode, beatId); `value` / `onChange`
 * work with the customer id, like a select.
 */
const CustomerSelect = ({ customers, value, onChange, required, placeholder = "Search customer code / name...", showScope = true, style }) => {
  const { salesmen, beats } = useReceivableLookups(["salesmen", "beats"]);
  const [scope, setScope] = useState({ salesmanUserId: "", beatIds: [] });
  const beatById = useMemo(() => new Map(beats.map((b) => [String(b.id), b])), [beats]);
  const scoped = customersInScope(customers, beats, scope);
  // keep the chosen customer selectable even when the filters would hide it
  const selected = customers.find((c) => String(c.id) === String(value));
  const list = selected && !scoped.some((c) => c.id === selected.id) ? [selected, ...scoped] : scoped;
  const options = list.map((c) => {
    const beat = beatById.get(String(c.beatId));
    return {
      value: c.id,
      label: customerLabel(c),
      sublabel: [beat?.name, beat?.salesmanName, c.phone].filter(Boolean).join(" · "),
    };
  });
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", ...style }}>
      {showScope ? <CustomerScopeFilters scope={scope} onChange={setScope} salesmen={salesmen} beats={beats} compact /> : null}
      <div style={{ minWidth: 260, flex: 1 }}>
        <SearchableSelect
          value={value}
          onChange={onChange}
          options={options}
          placeholder={`${placeholder}${scope.salesmanUserId || scope.beatIds.length ? ` (${scoped.length})` : ""}`}
          required={required}
        />
      </div>
    </div>
  );
};

export default CustomerSelect;
